import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crm_user") || "null"); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Accept temporary auth via ?print_token=xxx (server-side print rendering)
    let token = localStorage.getItem("crm_token");
    try {
      const url = new URL(window.location.href);
      const pt = url.searchParams.get("print_token");
      if (pt) token = pt;
    } catch { /* ignore */ }
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then(res => {
      setUser(res.data);
      localStorage.setItem("crm_user", JSON.stringify(res.data));
    }).catch(() => {
      // Don't clear localStorage if we were using a print_token
      const isPrint = new URL(window.location.href).searchParams.get("print_token");
      if (!isPrint) {
        localStorage.removeItem("crm_token");
        localStorage.removeItem("crm_user");
      }
      setUser(null);
    }).finally(() => setLoading(false));
  }, []);

  /**
   * login(email, password, opts?) → returns one of:
   *  { user }                           — fully logged in
   *  { totp_required: true }            — need to call again with totp_code
   *  { session_limit, active_sessions, device_type } — need to pick a session to terminate
   * Errors (rejected): error.response.data.detail = { code, message, ... }
   */
  const login = async (email, password, opts = {}) => {
    const body = { email, password, ...opts };
    let res;
    try {
      res = await api.post("/auth/login", body);
    } catch (e) {
      // 409 = session_limit (we want to expose it as a normal flow result)
      if (e?.response?.status === 409 && e.response?.data?.code === "session_limit") {
        return {
          session_limit: true,
          active_sessions: e.response.data.active_sessions || [],
          device_type: e.response.data.device_type,
          message: e.response.data.message,
        };
      }
      throw e;
    }
    if (res.data?.totp_required) {
      return { totp_required: true };
    }
    if (res.data?.access_token) {
      localStorage.setItem("crm_token", res.data.access_token);
      localStorage.setItem("crm_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      return { user: res.data.user };
    }
    return res.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("crm_user", JSON.stringify(res.data));
      return res.data;
    } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
